<script lang="ts">
    import { mdiClose } from "@mdi/js"
    import Icon from "./Icon.svelte"
    import { overlay } from "../../store"
    import { clickOutside } from "../../utils"
    import { fly } from "svelte/transition"
    import DealerLogin from "../ecommerce/login/DealerLogin.svelte"
    import Cart from "../ecommerce/cart/Cart.svelte"
    import MobileSearch from "./MobileSearch.svelte"
    import Content from "../routes/Content.svelte"
    import ProductInquiry from "../ecommerce/cart/ProductInquiry.svelte"
    import { iframeView } from "../../functionTools/iframeIntegration"
    import MobileLasersheetInput from "../ecommerce/product/detailed/MobileLasersheetInput.svelte"
    import { location } from "../../i18n"
    import DealerLoginMessage from "../ecommerce/login/DealerLoginMessage.svelte"
    import { get } from "svelte/store"
    import { cart, checkoutData } from "../../functionTools/cart"
    import { login } from "../../functionTools/login"
    import { defaultVAT } from "../../../../api/hooks/config-client"
    import { cartItemToInput, trackItemsEvent } from "../../lib/ga"

    // GA4 view_cart — einmal pro Öffnen der Warenkorb-Seitenleiste
    let cartViewFired = $state(false)
    $effect(() => {
        if ($overlay.show && $overlay.type == "cart") {
            if (!cartViewFired) {
                cartViewFired = true
                try {
                    const items = get(cart)
                    if (items?.length) {
                        const l = get(login)
                        const c = get(checkoutData)
                        trackItemsEvent(
                            "view_cart",
                            items.map(cartItemToInput),
                            l ? 0 : (c?.vat ?? defaultVAT)
                        )
                    }
                } catch {
                    // Tracking darf nichts blockieren
                }
            }
        } else {
            cartViewFired = false
        }
    })

    $effect(() => {
        if ($overlay && $overlay.type == "cart") return
        if ($overlay.show) document.body.style.overflow = "hidden"
        else document.body.style.overflow = "auto"
    })

    // Press-guard für backdrop: schließt nur wenn pointerdown UND click auf backdrop selbst waren
    let downOnBackdrop = $state(false)

    function handleBackdropPointerDown(e: PointerEvent) {
        if (e.target === e.currentTarget) {
            downOnBackdrop = true
        }
    }

    function handleBackdropClick(e: MouseEvent) {
        if (downOnBackdrop && e.target === e.currentTarget) {
            $overlay.show = false
        }
        downOnBackdrop = false
    }
</script>

{#if $overlay.show}
    <div
        class="sidebar-backdrop"
        role="presentation"
        aria-hidden="true"
        title="Schließen"
        data-testid="sidebar-backdrop"
        onpointerdown={handleBackdropPointerDown}
        onclick={handleBackdropClick}
    ></div>
    <div
        class:iframeView={$iframeView}
        class="sidebar-overlay {$overlay.type}"
        data-testid="sidebar-{$overlay.type}"
        transition:fly={{ x: 1000 }}
        use:clickOutside={(target) => {
            const dealerLoginLink = document.getElementById("dealerLoginIconLink")
            if (!dealerLoginLink?.contains(target)) $overlay.show = false
        }}
    >
        <a
            class="closer"
            href="/"
            onclick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                $overlay.show = false
            }}
        >
            <Icon path={mdiClose} size="24px" />
        </a>
        <div class="pad">
            {#if $overlay.type == "login"}
                <DealerLogin />
            {:else if $overlay.type == "logout"}
                <DealerLogin />
            {:else if $overlay.type == "dealerLoginMessage"}
                <DealerLoginMessage />
            {:else if $overlay.type == "cart"}
                <Cart checkoutButton={!$location.path?.includes("/checkout")} />
            {:else if $overlay.type == "search"}
                <MobileSearch />
            {:else if $overlay.type == "content"}
                {#if "pageToRender" in $overlay.properties}
                    <Content id={$overlay.properties.pageToRender} forceId={true} />
                {/if}
            {:else if $overlay.type == "inquiry"}
                {@const props = $overlay.properties as ProductInquiryProps}
                <ProductInquiry {...props} />
            {:else if $overlay.type == "lasersheet"}
                {@const props = $overlay.properties as LasersheetInput}
                <MobileLasersheetInput {...props} />
            {/if}
        </div>
    </div>
{/if}

<style lang="less" global>
    @import "../../css/variables.less";
    .sidebar-backdrop {
        position: fixed;
        inset: 0;
        z-index: 1999;
        background: rgba(0, 0, 0, 0.25);
        cursor:
            url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'><circle cx='16' cy='16' r='14' fill='black' fill-opacity='0.85'/><path d='M11 11 L21 21 M21 11 L11 21' stroke='white' stroke-width='2.5' stroke-linecap='round'/></svg>")
                16 16,
            default;
    }
    .sidebar-overlay {
        box-shadow: 10px 0px 0px 0px rgba(0, 0, 0, 0.25);
        .pad {
            padding: 1.2rem;
        }
        position: fixed;
        bottom: 0;
        left: initial;
        right: 0;
        width: 100vw;
        top: 0px;
        overflow-y: scroll;
        overflow-x: hidden;
        z-index: 2000;
        background: var(--surface);
        color: var(--on-surface);
        .content-block-wrapper-el {
            padding: 0px !important;
        }
        .closer {
            background: var(--background);
            color: var(--on-background);
            .flex();
            align-items: center;
            border-bottom: 1px solid var(--border-color-default);
            padding: 3px 20px;
            margin-bottom: 20px;
            font-size: 18px;
            position: sticky;
            top: 0;
            height: 65px;
            z-index: 9999;
        }

        &.lasersheet {
            .closer {
                background: white !important;
                position: fixed;
                width: fit-content;
                right: 1rem;
                justify-content: flex-end;
                border-bottom: none;
            }
        }

        @media @desktop {
            width: 50vw;
            border-left: 1px solid var(--border-color-default);
            overflow-y: auto;
        }
        @media @desktop_large {
            width: 50vw;
        }
        @media @desktop_ultralarge {
            width: 40vw;
        }
    }
    .iframeView {
        width: 100vw !important;
        @media @desktop {
            width: 100vw;
        }
    }
</style>
